// Lightweight i18n: a flat dictionary per language, no runtime i18n library
// (KISS). The active language is derived from the route prefix (useLang).

export type Lang = 'de' | 'en';

export interface Strings {
  langName: string;
  switchTo: string;
  nav: { home: string; privacy: string; imprint: string; passwords: string; sshKeys: string };
  hero: { title: string; subtitle: string; localBadge: string };
  tabs: { password: string; ssh: string };
  pw: {
    modeChars: string;
    modePhrase: string;
    modeMemorable: string;
    memorableHint: string;
    length: string;
    lowercase: string;
    uppercase: string;
    digits: string;
    symbols: string;
    excludeAmbiguous: string;
    presets: string;
    presetGood: string;
    presetStrong: string;
    presetParanoid: string;
    words: string;
    separator: string;
    sepHyphen: string;
    sepDot: string;
    sepSpace: string;
    capitalize: string;
    includeNumber: string;
    generate: string;
    regenerate: string;
    copy: string;
    copied: string;
    entropy: string;
    noClass: string;
    wordlistError: string;
  };
  strength: { weak: string; fair: string; strong: string; excellent: string };
  ssh: {
    keyType: string;
    ed25519: string;
    rsa: string;
    ed25519Hint: string;
    rsaHint: string;
    comment: string;
    commentPlaceholder: string;
    generate: string;
    generating: string;
    publicKey: string;
    privateKey: string;
    fingerprint: string;
    download: string;
    securityTitle: string;
    securityBody: string;
    passphraseHint: string;
    error: string;
  };
  seo: {
    aboutTitle: string;
    aboutBody: string;
    pwTitle: string;
    pwBody: string;
    sshTitle: string;
    sshBody: string;
    faqTitle: string;
    faq: { q: string; a: string }[];
  };
  footer: { rights: string; noStorage: string };
}

const de: Strings = {
  langName: 'Deutsch',
  switchTo: 'English',
  nav: {
    home: 'Generator',
    privacy: 'Datenschutz',
    imprint: 'Impressum',
    passwords: 'Passwörter',
    sshKeys: 'SSH-Keys',
  },
  hero: {
    title: 'SSH-Key-Generator',
    subtitle:
      'Sichere SSH-Schlüsselpaare (Ed25519, RSA-4096), erzeugt direkt in deinem Browser. Der private Schlüssel verlässt dein Gerät nie.',
    localBadge: '100 % lokal im Browser',
  },
  tabs: { password: 'Passwort', ssh: 'SSH-Key' },
  pw: {
    modeChars: 'Zeichen',
    modePhrase: 'Passphrase',
    modeMemorable: 'Merkbar',
    memorableHint: 'Leicht merkbare Wörter mit Zahlen und Sonderzeichen, z. B. hallo-wish-unter-12!2383',
    length: 'Länge',
    lowercase: 'Kleinbuchstaben (a–z)',
    uppercase: 'Großbuchstaben (A–Z)',
    digits: 'Zahlen (0–9)',
    symbols: 'Sonderzeichen (!@#…)',
    excludeAmbiguous: 'Mehrdeutige Zeichen ausschließen (0 O l 1 I)',
    presets: 'Voreinstellungen',
    presetGood: 'Gut',
    presetStrong: 'Stark',
    presetParanoid: 'Paranoid',
    words: 'Anzahl Wörter',
    separator: 'Trennzeichen',
    sepHyphen: 'Bindestrich',
    sepDot: 'Punkt',
    sepSpace: 'Leerzeichen',
    capitalize: 'Wortanfänge groß',
    includeNumber: 'Zahl anhängen',
    generate: 'Generieren',
    regenerate: 'Neu generieren',
    copy: 'Kopieren',
    copied: 'Kopiert',
    entropy: 'Entropie',
    noClass: 'Mindestens eine Zeichenklasse auswählen.',
    wordlistError: 'Wortliste konnte nicht geladen werden. Bitte neu versuchen.',
  },
  strength: { weak: 'Schwach', fair: 'Mittel', strong: 'Stark', excellent: 'Hervorragend' },
  ssh: {
    keyType: 'Schlüsseltyp',
    ed25519: 'Ed25519',
    rsa: 'RSA-4096',
    ed25519Hint: 'Moderner Standard, schnell und sicher. Empfohlen.',
    rsaHint: 'Maximale Kompatibilität mit älteren Systemen.',
    comment: 'Kommentar',
    commentPlaceholder: 'benutzer@rechner',
    generate: 'Schlüsselpaar erzeugen',
    generating: 'Erzeuge Schlüssel…',
    publicKey: 'Öffentlicher Schlüssel',
    privateKey: 'Privater Schlüssel',
    fingerprint: 'Fingerprint',
    download: 'Herunterladen',
    securityTitle: 'Dein Schlüssel bleibt bei dir',
    securityBody:
      'Die Erzeugung läuft vollständig in deinem Browser über die Web Crypto API. Der private Schlüssel wird niemals hochgeladen oder gespeichert.',
    passphraseHint:
      'Der private Schlüssel wird unverschlüsselt erzeugt. Für einen Passwortschutz führe lokal aus: ssh-keygen -p -f <datei>',
    error: 'Schlüsselerzeugung fehlgeschlagen. Bitte erneut versuchen.',
  },
  seo: {
    aboutTitle: 'Sicher generieren, ohne Spuren',
    aboutBody:
      'Dieser Generator erzeugt SSH-Schlüsselpaare ausschließlich lokal in deinem Browser. Es gibt keine serverseitige Verarbeitung und keine Speicherung. Schließe einfach den Tab, und nichts bleibt zurück.',
    pwTitle: 'Was macht ein sicheres Passwort aus?',
    pwBody:
      'Entscheidend sind Länge und Zufälligkeit. Je länger ein Passwort und je größer der verwendete Zeichenvorrat, desto höher die Entropie. Passphrasen aus mehreren zufälligen Wörtern sind leichter zu merken und bei genug Wörtern ähnlich stark.',
    sshTitle: 'Ed25519 oder RSA-4096?',
    sshBody:
      'Ed25519 ist der moderne Standard: kurze Schlüssel, hohe Sicherheit, schnelle Operationen. RSA-4096 bietet maximale Kompatibilität mit älteren Servern. Wähle Ed25519, wenn nichts dagegen spricht.',
    faqTitle: 'Häufige Fragen',
    faq: [
      {
        q: 'Wird mein privater Schlüssel auf einem Server gespeichert?',
        a: 'Nein. Die Erzeugung läuft vollständig lokal im Browser über die Web Crypto API. Der private Schlüssel wird nie übertragen oder gespeichert.',
      },
      {
        q: 'Ed25519 oder RSA-4096 — was soll ich wählen?',
        a: 'Ed25519 ist der moderne Standard und für die meisten Fälle die beste Wahl. RSA-4096 nutzt du nur, wenn ein älteres System Ed25519 nicht unterstützt.',
      },
      {
        q: 'Wie verwende ich den Schlüssel?',
        a: 'Lade den privaten Schlüssel herunter, setze die Dateirechte (chmod 600) und füge den öffentlichen Schlüssel auf dem Server zu ~/.ssh/authorized_keys hinzu.',
      },
    ],
  },
  footer: {
    rights: 'Kostenloses Werkzeug von ecosquirrel.',
    noStorage: 'Keine serverseitige Speicherung. Keine Übertragung deiner Schlüssel.',
  },
};

const en: Strings = {
  langName: 'English',
  switchTo: 'Deutsch',
  nav: {
    home: 'Generator',
    privacy: 'Privacy',
    imprint: 'Imprint',
    passwords: 'Passwords',
    sshKeys: 'SSH keys',
  },
  hero: {
    title: 'SSH Key Generator',
    subtitle:
      'Secure SSH key pairs (Ed25519, RSA-4096), generated right in your browser. The private key never leaves your device.',
    localBadge: '100% local in your browser',
  },
  tabs: { password: 'Password', ssh: 'SSH key' },
  pw: {
    modeChars: 'Characters',
    modePhrase: 'Passphrase',
    modeMemorable: 'Memorable',
    memorableHint: 'Easy words with numbers and a symbol, e.g. hallo-wish-unter-12!2383',
    length: 'Length',
    lowercase: 'Lowercase (a–z)',
    uppercase: 'Uppercase (A–Z)',
    digits: 'Digits (0–9)',
    symbols: 'Symbols (!@#…)',
    excludeAmbiguous: 'Exclude ambiguous characters (0 O l 1 I)',
    presets: 'Presets',
    presetGood: 'Good',
    presetStrong: 'Strong',
    presetParanoid: 'Paranoid',
    words: 'Number of words',
    separator: 'Separator',
    sepHyphen: 'Hyphen',
    sepDot: 'Dot',
    sepSpace: 'Space',
    capitalize: 'Capitalize words',
    includeNumber: 'Append a number',
    generate: 'Generate',
    regenerate: 'Regenerate',
    copy: 'Copy',
    copied: 'Copied',
    entropy: 'Entropy',
    noClass: 'Select at least one character class.',
    wordlistError: 'Could not load the word list. Please try again.',
  },
  strength: { weak: 'Weak', fair: 'Fair', strong: 'Strong', excellent: 'Excellent' },
  ssh: {
    keyType: 'Key type',
    ed25519: 'Ed25519',
    rsa: 'RSA-4096',
    ed25519Hint: 'Modern standard, fast and secure. Recommended.',
    rsaHint: 'Maximum compatibility with older systems.',
    comment: 'Comment',
    commentPlaceholder: 'user@host',
    generate: 'Generate key pair',
    generating: 'Generating key…',
    publicKey: 'Public key',
    privateKey: 'Private key',
    fingerprint: 'Fingerprint',
    download: 'Download',
    securityTitle: 'Your key stays with you',
    securityBody:
      'Generation runs entirely in your browser via the Web Crypto API. The private key is never uploaded or stored.',
    passphraseHint:
      'The private key is generated unencrypted. To add a passphrase, run locally: ssh-keygen -p -f <file>',
    error: 'Key generation failed. Please try again.',
  },
  seo: {
    aboutTitle: 'Generate securely, leave no trace',
    aboutBody:
      'This generator creates SSH key pairs exclusively in your browser. There is no server-side processing and no storage. Just close the tab and nothing remains.',
    pwTitle: 'What makes a password secure?',
    pwBody:
      'Length and randomness are what matter. The longer a password and the larger the character pool, the higher the entropy. Passphrases of several random words are easier to remember and similarly strong with enough words.',
    sshTitle: 'Ed25519 or RSA-4096?',
    sshBody:
      'Ed25519 is the modern standard: short keys, strong security, fast operations. RSA-4096 offers maximum compatibility with older servers. Pick Ed25519 unless something requires otherwise.',
    faqTitle: 'Frequently asked questions',
    faq: [
      {
        q: 'Is my private key stored on a server?',
        a: 'No. Generation runs entirely in your browser via the Web Crypto API. The private key is never transmitted or stored.',
      },
      {
        q: 'Ed25519 or RSA-4096 — which should I pick?',
        a: 'Ed25519 is the modern standard and the best choice for most cases. Use RSA-4096 only when an older system does not support Ed25519.',
      },
      {
        q: 'How do I use the key?',
        a: 'Download the private key, set its permissions (chmod 600), and add the public key to ~/.ssh/authorized_keys on the server.',
      },
    ],
  },
  footer: {
    rights: 'A free tool by ecosquirrel.',
    noStorage: 'No server-side storage. Your keys are never transmitted.',
  },
};

export const STRINGS: Record<Lang, Strings> = { de, en };
